/* Offline support: cached protocol library, client-side selection mirror,
 * and an outbox that syncs offline check-ins when connectivity returns. */
import { http } from "@/lib/api";

const LIB_KEY = "ss_lib";
const OUT_KEY = "ss_outbox";
const SECS = { t60: 60, t180: 180, t600: 600, t1200: 1200, cannot_stop: 0 };
const DUR_LABELS = { 60: "60 seconds", 180: "3 minutes", 600: "10 minutes", 1200: "20 minutes" };

export const uuid = () =>
  (crypto.randomUUID && crypto.randomUUID()) || `off_${Date.now()}_${Math.random().toString(16).slice(2)}`;

export function setLib(lib) {
  try { localStorage.setItem(LIB_KEY, JSON.stringify(lib)); } catch (e) {}
}
export function getLib() {
  try { return JSON.parse(localStorage.getItem(LIB_KEY)); } catch (e) { return null; }
}

export async function loadLib() {
  try {
    const { data } = await http.get("/protocols-public");
    setLib(data);
    return data;
  } catch (e) {
    return getLib();
  }
}

export function durationLabel(p) {
  return p.while_functioning ? "While you keep going" : (DUR_LABELS[p.duration_seconds] || `${p.duration_seconds} seconds`);
}

// Mirrors the backend select_protocol (without server-side feedback/recency deltas).
export function selectLocal(mode, ctxId, timeId, lib, { excludeIds = [], maxDuration = null } = {}) {
  const secs = timeId ? SECS[timeId] : null;
  const cannotStop = timeId === "cannot_stop";
  let cands = (lib.protocols || []).filter((p) => p.mode === mode && p.active !== false);
  if (ctxId) cands = cands.filter((p) => !(p.incompatible || []).includes(ctxId));

  if (cannotStop) {
    const wf = cands.filter((p) => p.while_functioning);
    cands = wf.length ? wf : [...cands].sort((a, b) => a.duration_seconds - b.duration_seconds).slice(0, 3);
  } else if (secs != null) {
    cands = cands.filter((p) => p.duration_seconds <= secs);
  }
  if (maxDuration != null) cands = cands.filter((p) => p.duration_seconds < maxDuration);
  if (!cands.length) {
    let pool = (lib.protocols || []).filter((p) => p.mode === mode && p.active !== false);
    cands = (secs != null && !cannotStop) ? pool.filter((p) => p.duration_seconds <= secs) : pool;
    if (!cands.length) cands = [...pool].sort((a, b) => a.duration_seconds - b.duration_seconds).slice(0, 1);
  }
  const score = (p) => {
    let s = p.priority || 0;
    if (ctxId && (p.contexts || []).includes(ctxId)) s += 100;
    if (excludeIds.includes(p.id)) s -= 1000;
    return s;
  };
  const chosen = [...cands].sort((a, b) => score(b) - score(a))[0];
  return { ...chosen, duration_label: durationLabel(chosen) };
}

export function buildResultLocal(mode, ctx, protocol, lib) {
  const copy = (lib.mode_copy && lib.mode_copy[mode]) || {};
  const rc = (lib.reported_change && lib.reported_change[mode]) || [];
  return {
    checkin_id: uuid(),
    _offline: true,
    is_baseline: false,
    mode,
    mode_label: copy.label || mode,
    mode_purpose: copy.purpose,
    observation: copy.observation,
    demand_line: ctx ? `This comes at a time when you need to ${ctx.lower || ctx.title}.` : null,
    validation: copy.validation,
    permission: copy.permission,
    reported_change_options: rc,
    protocol,
  };
}

export function supportTextLocal(lib, mode, audience) {
  const t = (lib.support_templates && lib.support_templates[mode]) || {};
  return t[audience] || t.personal || "";
}

/* ---------------- Outbox ---------------- */
function readOutbox() {
  try { return JSON.parse(localStorage.getItem(OUT_KEY)) || []; } catch (e) { return []; }
}
function writeOutbox(list) {
  try { localStorage.setItem(OUT_KEY, JSON.stringify(list)); } catch (e) {}
}

export function enqueue(record) {
  const list = readOutbox();
  list.push(record);
  writeOutbox(list);
}

export function updateRecord(id, patch) {
  const list = readOutbox();
  const i = list.findIndex((r) => r.id === id);
  if (i >= 0) { list[i] = { ...list[i], ...patch }; writeOutbox(list); }
}

export function outboxCount() {
  return readOutbox().length;
}

export async function flushOutbox() {
  const list = readOutbox();
  if (!list.length) return 0;
  const remaining = [];
  let synced = 0;
  for (const rec of list) {
    try {
      await http.post("/checkin/offline", rec);
      synced += 1;
    } catch (e) {
      remaining.push(rec); // keep for next attempt
    }
  }
  writeOutbox(remaining);
  return synced;
}
